import {
  addFinalizedMealPlanItem,
  addGenericMealPlanItem,
  addRecipeToGenericMealPlan,
  addMealPlanTemplateItem,
  customizeMealPlanTemplateItem,
  addManualMealPlanItem,
  customizeFinalizedMealPlanItem,
  updateFinalizedMealPlanItem,
  updateGenericMealPlanIngredient,
  updateGenericMealPlanItem,
  updateMealPlanTemplateItem,
  updateManualMealPlanIngredient,
  updateManualMealPlanServings,
} from '@/lib/api/mealPlans';
import { showWarningToast } from '@/utils/toast-utils';
import type { ExpandedFoodRow, FoodSearchResult, QuantitySyncField } from './nutritionalAnalysisTypes';

export type GenericOptionAddTarget = {
  optionGroupId: string;
  optionGroupLabel: string;
  selectedOptionGroupId?: string | null;
};

export async function runManualOrFinalizedFoodSelection(params: {
  resolvedPlanId: string;
  selectedDayNumber: number;
  mealName: string;
  planStatus: string;
  selectedItem: FoodSearchResult;
  normalizedFoodName: string;
  selectedId: string;
  provider: 'spoonacular' | 'internal';
  source: 'cached' | 'api';
  resolveMealApiName: (mealName: string) => string;
  resolveFinalizedMealType: (mealName: string) => 'breakfast' | 'lunch' | 'dinner' | 'snack';
  finalizedMealId?: string;
  rowsByMeal: Record<string, ExpandedFoodRow[]>;
  isIngredientRow: (row: Pick<ExpandedFoodRow, 'itemType' | 'ingredientId'>) => boolean;
  defaultAddIngredientServingId: string;
  defaultAddRecipeServingId: string;
  preferredIngredientServingId?: string;
  genericOptionTarget?: GenericOptionAddTarget;
}): Promise<{ skippedWithWarning: boolean }> {
  const {
    resolvedPlanId,
    selectedDayNumber,
    mealName,
    planStatus,
    selectedItem,
    normalizedFoodName,
    selectedId,
    provider,
    source,
    resolveMealApiName,
    resolveFinalizedMealType,
    finalizedMealId,
    rowsByMeal,
    isIngredientRow,
    defaultAddIngredientServingId,
    defaultAddRecipeServingId,
    preferredIngredientServingId,
    genericOptionTarget,
  } = params;
  const ingredientServingId = String(preferredIngredientServingId ?? '').trim() || defaultAddIngredientServingId;

  if (planStatus === 'draft') {
    const response = await addManualMealPlanItem(
      selectedItem.type === 'ingredient'
        ? {
            draftId: resolvedPlanId,
            day: selectedDayNumber,
            mealName: resolveMealApiName(mealName),
            itemType: 'ingredient',
            ingredient: {
              id: selectedId,
              provider,
              amount: 1,
              servingId: ingredientServingId,
            },
          }
        : {
            draftId: resolvedPlanId,
            day: selectedDayNumber,
            mealName: resolveMealApiName(mealName),
            itemType: 'recipe',
            recipe: {
              id: selectedId,
              provider,
              source,
            },
            nutritionServings: 1,
            portionSizeMultiplier: 1,
            selectedServingId: defaultAddRecipeServingId,
          },
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to add food item');
    }
    return { skippedWithWarning: false };
  }

  if (planStatus === 'generic') {
    const response = selectedItem.type === 'ingredient'
      ? await addGenericMealPlanItem({
          genericPlanId: resolvedPlanId,
          mealName: resolveMealApiName(mealName),
          itemType: 'ingredient',
          ingredient: {
            id: selectedId,
            provider,
            amount: 1,
            servingId: ingredientServingId,
          },
          optionGroupId: genericOptionTarget?.optionGroupId,
          optionGroupLabel: genericOptionTarget?.optionGroupLabel,
          selectedOptionGroupId: genericOptionTarget?.selectedOptionGroupId,
        })
      : await addRecipeToGenericMealPlan({
          genericPlanId: resolvedPlanId,
          mealName: resolveMealApiName(mealName),
          recipe: {
            id: selectedId,
            provider,
            source,
            optionGroupId: genericOptionTarget?.optionGroupId,
            optionGroupLabel: genericOptionTarget?.optionGroupLabel,
          },
          nutritionServings: 1,
          portionSizeMultiplier: 1,
          selectedServingId: defaultAddRecipeServingId,
          optionGroupId: genericOptionTarget?.optionGroupId,
          optionGroupLabel: genericOptionTarget?.optionGroupLabel,
          selectedOptionGroupId: genericOptionTarget?.selectedOptionGroupId,
        });

    if (!response.success) {
      throw new Error(response.message || 'Failed to add food item');
    }
    return { skippedWithWarning: false };
  }

  if (planStatus === 'template') {
    const response = selectedItem.type === 'ingredient'
      ? await addMealPlanTemplateItem(resolvedPlanId, {
        dayNumber: selectedDayNumber,
        mealName: resolveMealApiName(mealName),
        itemType: 'ingredient',
        ingredient: {
          id: selectedId,
          provider,
          amount: 1,
          servingId: ingredientServingId,
        },
      })
      : await addMealPlanTemplateItem(resolvedPlanId, {
        dayNumber: selectedDayNumber,
        mealName: resolveMealApiName(mealName),
        itemType: 'recipe',
        recipe: {
          id: selectedId,
          provider,
          source,
        },
        nutritionServings: 1,
        selectedServingId: defaultAddRecipeServingId,
        portionSizeMultiplier: 1,
      });

    if (!response.success) {
      throw new Error(response.message || 'Failed to add food item');
    }
    return { skippedWithWarning: false };
  }

  const response = selectedItem.type === 'ingredient'
    ? await (async () => {
      const rows = rowsByMeal[mealName] ?? [];
      const targetRecipeRow = rows.find(
        (row) => !isIngredientRow(row) && String(row.itemId ?? '').trim().length > 0,
      );
      if (!targetRecipeRow?.itemId) {
        showWarningToast(
          'Add a recipe first',
          'Ingredients can be added to finalized plans only within an existing recipe item.',
        );
        return null;
      }
      return customizeFinalizedMealPlanItem(resolvedPlanId, {
        itemId: targetRecipeRow.itemId,
        overrides: [
          {
            action: 'add',
            replacementIngredientId: selectedId,
            replacementName: normalizedFoodName,
            newAmount: 1,
            servingId: ingredientServingId,
            reason: 'preference',
          },
        ],
      });
    })()
    : await addFinalizedMealPlanItem(resolvedPlanId, {
      dayNumber: selectedDayNumber,
      ...(finalizedMealId
        ? { mealId: finalizedMealId }
        : { mealType: resolveFinalizedMealType(resolveMealApiName(mealName)) }),
      recipe: {
        id: selectedId,
        provider,
        source,
      },
      servings: 1,
      optionGroupId: genericOptionTarget?.optionGroupId,
      optionGroupLabel: genericOptionTarget?.optionGroupLabel,
      selectedOptionGroupId: genericOptionTarget?.selectedOptionGroupId,
    });

  if (!response) return { skippedWithWarning: true };
  if (!response.success) {
    throw new Error(response.message || 'Failed to add food item');
  }

  return { skippedWithWarning: false };
}

export async function runManualOrFinalizedIngredientSelection(params: {
  resolvedPlanId: string;
  selectedDayNumber: number;
  mealName: string;
  planStatus: string;
  normalizedIngredientName: string;
  selectedId: string;
  provider: 'spoonacular' | 'internal';
  resolveMealApiName: (mealName: string) => string;
  finalizedMealId?: string;
  insideRecipe?: boolean;
  rowsByMeal: Record<string, ExpandedFoodRow[]>;
  isIngredientRow: (row: Pick<ExpandedFoodRow, 'itemType' | 'ingredientId'>) => boolean;
  defaultAddIngredientServingId: string;
  preferredIngredientServingId?: string;
  genericOptionTarget?: GenericOptionAddTarget;
}): Promise<{ skippedWithWarning: boolean }> {
  const {
    resolvedPlanId,
    selectedDayNumber,
    mealName,
    planStatus,
    normalizedIngredientName,
    selectedId,
    provider,
    resolveMealApiName,
    finalizedMealId,
    insideRecipe = false,
    rowsByMeal,
    isIngredientRow,
    defaultAddIngredientServingId,
    preferredIngredientServingId,
    genericOptionTarget,
  } = params;
  const ingredientServingId = String(preferredIngredientServingId ?? '').trim() || defaultAddIngredientServingId;

  if (planStatus === 'draft') {
    const response = await addManualMealPlanItem({
      draftId: resolvedPlanId,
      day: selectedDayNumber,
      mealName: resolveMealApiName(mealName),
      itemType: 'ingredient',
      ingredient: {
        id: selectedId,
        provider,
        amount: 1,
        servingId: ingredientServingId,
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to add ingredient');
    }
    return { skippedWithWarning: false };
  }

  if (planStatus === 'generic') {
    const response = await addGenericMealPlanItem({
      genericPlanId: resolvedPlanId,
      mealName: resolveMealApiName(mealName),
      itemType: 'ingredient',
      ingredient: {
        id: selectedId,
        provider,
        amount: 1,
        servingId: ingredientServingId,
      },
      optionGroupId: genericOptionTarget?.optionGroupId,
      optionGroupLabel: genericOptionTarget?.optionGroupLabel,
      selectedOptionGroupId: genericOptionTarget?.selectedOptionGroupId,
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to add ingredient');
    }
    return { skippedWithWarning: false };
  }

  if (planStatus === 'template' && !insideRecipe) {
    const response = await addMealPlanTemplateItem(resolvedPlanId, {
      dayNumber: selectedDayNumber,
      mealName: resolveMealApiName(mealName),
      itemType: 'ingredient',
      ingredient: {
        id: selectedId,
        provider,
        amount: 1,
        servingId: ingredientServingId,
      },
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to add ingredient');
    }
    return { skippedWithWarning: false };
  }

  if (!insideRecipe) {
    const mealApiName = String(resolveMealApiName(mealName) || mealName).trim().toLowerCase();
    const mealTypeFromName = mealApiName
      .replace(/_\d+$/, '')
      .replace(/[^a-z]/g, '');
    const mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack' = (
      mealTypeFromName.includes('breakfast')
        ? 'breakfast'
        : mealTypeFromName.includes('lunch')
          ? 'lunch'
          : mealTypeFromName.includes('dinner')
            ? 'dinner'
            : 'snack'
    );

    const response = await addFinalizedMealPlanItem(resolvedPlanId, {
      dayNumber: selectedDayNumber,
      ...(finalizedMealId
        ? { mealId: finalizedMealId }
        : { mealType }),
      itemType: 'ingredient',
      ingredient: {
        id: selectedId,
        provider,
        amount: 1,
        servingId: ingredientServingId,
      },
      notes: 'Added via API test',
      optionGroupId: genericOptionTarget?.optionGroupId,
      optionGroupLabel: genericOptionTarget?.optionGroupLabel,
      selectedOptionGroupId: genericOptionTarget?.selectedOptionGroupId,
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to add ingredient');
    }
    return { skippedWithWarning: false };
  }

  const rows = rowsByMeal[mealName] ?? [];
  const targetRecipeRow = rows.find(
    (row) => !isIngredientRow(row) && String(row.itemId ?? '').trim().length > 0,
  );
  if (!targetRecipeRow?.itemId) {
    showWarningToast(
      'Add a recipe first',
      'Ingredients can be added to finalized plans only within an existing recipe item.',
    );
    return { skippedWithWarning: true };
  }

  const response = await (planStatus === 'template' ? customizeMealPlanTemplateItem : customizeFinalizedMealPlanItem)(resolvedPlanId, {
    itemId: targetRecipeRow.itemId,
    overrides: [
      {
        action: 'add',
        replacementIngredientId: selectedId,
        replacementName: normalizedIngredientName,
        newAmount: 1,
        servingId: ingredientServingId,
        reason: 'preference',
      },
    ],
  });
  if (!response.success) {
    throw new Error(response.message || 'Failed to add ingredient');
  }

  return { skippedWithWarning: false };
}

export async function runManualOrFinalizedQuantitySync(params: {
  resolvedPlanId: string;
  selectedDayNumber: number;
  planStatus: string;
  apiMealName: string;
  targetRow: ExpandedFoodRow;
  rowsInMeal: ExpandedFoodRow[];
  resolvedItemId: string;
  quantity: number;
  defaultAddIngredientServingId: string;
  parseRecipeServingId: (value: unknown) => string | undefined;
  isIngredientRow: (row: Pick<ExpandedFoodRow, 'itemType' | 'ingredientId'>) => boolean;
  syncField?: QuantitySyncField;
}): Promise<void> {
  const {
    resolvedPlanId,
    selectedDayNumber,
    planStatus,
    apiMealName,
    targetRow,
    rowsInMeal,
    resolvedItemId,
    quantity,
    defaultAddIngredientServingId,
    parseRecipeServingId,
    isIngredientRow,
    syncField,
  } = params;

  const ingredientRow = isIngredientRow(targetRow);
  const safeQuantity = quantity > 0 ? quantity : 0.1;
  const normalizedItemId = String(targetRow.itemId ?? '').trim();
  const normalizedRecipeId = String(targetRow.recipeId ?? resolvedItemId).trim();
  const normalizedIngredientId = String(targetRow.ingredientId ?? '').trim();

  if (!ingredientRow) {
    const normalizedSource = String(targetRow.source ?? '').trim().toLowerCase();
    const isImportedRecipeRow =
      targetRow.fromManualSlotItem === true
      || normalizedSource === 'internal'
      || Boolean(normalizedItemId && normalizedRecipeId && normalizedItemId !== normalizedRecipeId);

    // Imported recipe rows (especially from AI/manual recipe import) should persist via manual update-servings.
    if (planStatus !== 'generic' && isImportedRecipeRow && normalizedRecipeId) {
      const response = await updateManualMealPlanServings({
        draftId: resolvedPlanId,
        day: selectedDayNumber,
        mealName: apiMealName,
        itemId: normalizedItemId || normalizedRecipeId,
        recipeId: normalizedRecipeId,
        nutritionServings: safeQuantity,
        servings: safeQuantity,
        selectedServingId: parseRecipeServingId(targetRow.serving),
      });
      if (!response.success) {
        throw new Error(response.message || 'Failed to update imported recipe servings');
      }
      return;
    }
  }

  if (planStatus === 'generic') {
    const response = ingredientRow
      ? await updateGenericMealPlanIngredient({
          genericPlanId: resolvedPlanId,
          mealName: apiMealName,
          itemId: normalizedItemId || resolvedItemId,
          ingredientId: normalizedIngredientId || resolvedItemId,
          amount: safeQuantity,
          servingId: targetRow.unit || defaultAddIngredientServingId,
        })
      : await updateGenericMealPlanItem({
          genericPlanId: resolvedPlanId,
          mealName: apiMealName,
          itemId: resolvedItemId,
          nutritionServings: safeQuantity,
          servings: safeQuantity,
          portionSizeMultiplier: 1,
          selectedServingId: parseRecipeServingId(targetRow.serving),
        });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update quantity');
    }
    return;
  }

  if (planStatus === 'draft') {
    const response =
      ingredientRow
        ? await updateManualMealPlanIngredient({
            draftId: resolvedPlanId,
            day: selectedDayNumber,
            mealName: apiMealName,
            itemId: normalizedItemId || resolvedItemId,
            ingredientId: normalizedIngredientId || resolvedItemId,
            amount: safeQuantity,
            servingId: targetRow.unit || defaultAddIngredientServingId,
          })
        : await updateManualMealPlanServings({
            draftId: resolvedPlanId,
            day: selectedDayNumber,
            mealName: apiMealName,
            itemId: resolvedItemId,
            recipeId: targetRow.recipeId ?? resolvedItemId,
            nutritionServings: safeQuantity,
            servings: safeQuantity,
            selectedServingId: parseRecipeServingId(targetRow.serving),
          });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update quantity');
    }
    return;
  }

  if (ingredientRow) {
    const customizeItem =
      planStatus === 'template' ? customizeMealPlanTemplateItem : customizeFinalizedMealPlanItem;
    const standaloneIngredientItemId = String(targetRow.itemId ?? '').trim();
    const recipeIdForIngredient = String(targetRow.recipeId ?? '').trim();
    if (standaloneIngredientItemId && !recipeIdForIngredient) {
      const response = await customizeItem(resolvedPlanId, {
        itemId: standaloneIngredientItemId,
        overrides: [
          {
            action: 'modify',
            newAmount: safeQuantity,
            servingId: String(targetRow.unit ?? '').trim() || defaultAddIngredientServingId,
          },
        ],
      });
      if (!response.success) {
        throw new Error(response.message || 'Failed to update ingredient');
      }
      return;
    }

    const customizationItemId = String(
      rowsInMeal.find((row) => !isIngredientRow(row) && String(row.itemId ?? '').trim().length > 0)?.itemId
      ?? targetRow.recipeId
      ?? targetRow.itemId
      ?? '',
    ).trim();
    const originalIngredientId = String(targetRow.ingredientId ?? targetRow.itemId ?? '').trim();
    if (!customizationItemId || !originalIngredientId) return;

    const response = await customizeItem(resolvedPlanId, {
      itemId: customizationItemId,
      overrides: [
        {
          action: 'modify',
          originalIngredientId,
          newAmount: safeQuantity,
          servingId: String(targetRow.unit ?? '').trim() || defaultAddIngredientServingId,
        },
      ],
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update ingredient');
    }
    return;
  }

  const updateItem = planStatus === 'template' ? updateMealPlanTemplateItem : updateFinalizedMealPlanItem;
  const response = await updateItem(resolvedPlanId, {
    itemId: resolvedItemId,
    nutritionServings: safeQuantity,
    servings: safeQuantity,
  });
  if (!response.success) {
    throw new Error(response.message || 'Failed to update quantity');
  }
}
